import styled from '@emotion/styled'
import { Combobox as HeadlessCombobox } from '@headlessui/react'
import React, { useRef, useState } from 'react'
import { z } from 'zod'

import { ComboboxOption } from './Option'
import { theme } from '../../constants'
import {
  DropdownItemsStylesProps,
  InputStylesProps,
  comboboxInputStyles,
  dropdownItemsStyles,
} from '../../constants/styles'
import { useValidate } from '../../hooks/useValidate'
import { useTranslations } from '../../translate'
import { ErrorMessage } from '../ErrorMessage'
import { Form } from '../Form'
import { InputAutoComplete } from '../Form/Input'
import { Icon, IconName } from '../Icon'

const Button = styled(HeadlessCombobox.Button)({
  display: 'none',
}) as typeof HeadlessCombobox.Button

const ComboboxElement = styled.div({
  position: 'relative',
})

const ComboboxInput = styled<any>(HeadlessCombobox.Input, {
  shouldForwardProp: (propName) => !['small', 'invalid'].includes(propName),
})(comboboxInputStyles) as
  | typeof HeadlessCombobox.Input
  | ((props: InputStylesProps) => JSX.Element)

const Options = styled(HeadlessCombobox.Options)<
  DropdownItemsStylesProps & { alignRight?: boolean }
>(({ alignRight, ...props }) => ({
  ...dropdownItemsStyles(props),
  ...(alignRight && {
    left: undefined,
    right: 0,
  }),
  zIndex: theme.zIndex.popover,
  // width: 'var(--button-width)' // NOT DEFINED!
}))

const InputContainer = styled.div({
  position: 'relative',
})

const StyledIcon = styled(Icon)({
  marginLeft: 8,
  pointerEvents: 'none',
  position: 'absolute',
  right: 4,
  top: '50%',
  transform: 'translate(-50%, -50%)',
})

export type ComboboxProps<T = string> = {
  autoComplete?: InputAutoComplete
  disabled?: boolean
  displayValue?: string
  label?: string
  labelIcon?: IconName
  options: ComboboxOption<T>[]
  placeholder?: string
  minWidth?: number
  maxWidth?: number
  required?: boolean
  small?: boolean
  schema?: z.ZodTypeAny
  errorMessage?: string
  reserveErrorSpace?: boolean
  forceShowError?: boolean
  alignRight?: boolean
} & (
  | {
      clearable?: false
      onChange: (value: T) => void
      value: T
    }
  | {
      clearable: true
      onChange: (value: T | undefined) => void
      value: T | undefined
    }
)

export const Combobox = <T,>(props: ComboboxProps<T>) => {
  const [touched, setTouched] = useState(false)

  const validateError = useValidate(props.schema, props.required, props.value)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [query, setQuery] = useState('')
  const t = useTranslations()

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!event.target.value && props.clearable) {
      props.onChange(undefined)
    }
  }

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTouched(true)
    setQuery(event.target.value)
  }

  const handleValueChange = (value: T | null) => {
    if (props.clearable) {
      props.onChange(value ?? undefined)
    } else if (value !== null && value !== undefined) {
      props.onChange(value)
    }

    setQuery('')
  }

  const displayValue =
    props.displayValue ??
    props.options.find((option) => option.value === props.value)?.label ??
    ''

  const filteredOptions = props.options.filter((option) => {
    return option.label
      ?.trim()
      .toLowerCase()
      .includes(query.trim().toLowerCase())
  })

  const placeholder =
    props.placeholder ||
    (props.label ? `${props.label}...` : `${t.form.select_a_value()}...`)

  /**
   * Workaround for https://github.com/tailwindlabs/headlessui/discussions/1236.
   *
   * Makes the options open on focus.
   */
  const open = (isOpen: boolean, event?: React.FocusEvent) => {
    // !e -> not FocusEvent -> we should click input
    // e.relatedTarget -> there is an element losing focus (Option focused) -> don't click input
    if (!isOpen && (!event || event.relatedTarget)) {
      buttonRef.current?.click()
    }

    setQuery('')
  }

  const errorMessage = props.errorMessage ?? validateError
  const showError = (touched || props.forceShowError) && !!errorMessage

  return (
    <HeadlessCombobox
      disabled={props.disabled}
      onChange={handleValueChange}
      value={props.value ?? null}
    >
      {({ open: isOpen }) => {
        const comboboxElement = (
          <ComboboxElement>
            <InputContainer>
              <ComboboxInput
                // We'll use "nope" as a nonsense value, as some browsers ignore "off":
                autoComplete={'nope'}
                cursor={isOpen ? 'text' : 'pointer'}
                displayValue={() => displayValue}
                invalid={showError}
                onBlur={handleBlur}
                onChange={handleQueryChange}
                onFocus={(event) => open(isOpen, event)}
                onKeyDown={() => open(isOpen)}
                onMouseDown={() => open(isOpen)}
                placeholder={placeholder}
                // @ts-ignore "small is not valid HTML attribute"
                small={props.small}
                minWidth={props.minWidth}
                maxWidth={props.maxWidth}
                spellCheck="false"
              />
              <StyledIcon name="ChevronDownIcon" size="sm" />
            </InputContainer>
            <Button ref={buttonRef} />
            {filteredOptions.length >= 1 && (
              <InputContainer>
                <ErrorMessage
                  errorMessage={showError ? errorMessage : undefined}
                  reserveSpace={props.reserveErrorSpace}
                />
                <Options alignRight={props.alignRight}>
                  {props.clearable && (
                    <ComboboxOption
                      label={placeholder}
                      placeholder
                      small={props.small}
                      value={null}
                    />
                  )}
                  {filteredOptions.map((option) => (
                    <ComboboxOption
                      label={option.label}
                      key={String(option.value)}
                      small={props.small}
                      value={option.value}
                    />
                  ))}
                </Options>
              </InputContainer>
            )}
          </ComboboxElement>
        )

        return props.label ? (
          <Form.Label
            icon={props.labelIcon}
            required={props.required}
            text={props.label}
          >
            {comboboxElement}
          </Form.Label>
        ) : (
          comboboxElement
        )
      }}
    </HeadlessCombobox>
  )
}
